import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Clock, MapPin, UtensilsCrossed, Wine, Coffee, Leaf, ChefHat, Star } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SEOHead, RestaurantSchema, BreadcrumbSchema, SpeakableSchema } from '../components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback data in case API fails
const fallbackRestaurants = [
  {
    id: 'emerald-dining',
    name: 'Emerald Dining Room',
    description: 'Our signature restaurant offering a fusion of international and regional cuisines',
    image: 'https://images.pexels.com/photos/29685045/pexels-photo-29685045.jpeg?auto=compress&cs=tinysrgb&w=800',
    hours: '7:00 AM - 10:30 PM',
    cuisine: 'Multi-Cuisine',
    dress_code: 'Smart Casual',
    features: ['Live Kitchen', 'Mountain Views', 'Al Fresco Seating'],
  },
  {
    id: 'koraput-kitchen',
    name: 'Koraput Kitchen',
    description: 'Authentic tribal and Odia delicacies crafted with local ingredients',
    image: 'https://images.unsplash.com/photo-1716535232842-d10da4eb33d5?w=800&fit=crop',
    hours: '12:30 PM - 10:00 PM',
    cuisine: 'Odia Traditional',
    dress_code: 'Casual',
    features: ['Local Ingredients', 'Traditional Recipes', 'Cultural Experience'],
  },
  {
    id: 'skybar',
    name: 'The Sky Lounge',
    description: 'Rooftop bar with panoramic views and curated cocktails',
    image: 'https://images.pexels.com/photos/9609856/pexels-photo-9609856.jpeg?auto=compress&cs=tinysrgb&w=800',
    hours: '5:00 PM - 11:00 PM',
    cuisine: 'Bar & Tapas',
    dress_code: 'Smart Casual',
    features: ['Sunset Views', 'Signature Cocktails', 'Live Music Weekends'],
  },
];

const fallbackMenu = {
  breakfast: [
    { name: 'Traditional Odia Thali', price: 450, description: 'Pakhala, dalma, saga bhaja, and more', is_signature: true, is_vegetarian: true },
    { name: 'Continental Breakfast', price: 550, description: 'Eggs, toast, bacon, and fresh juice', is_signature: false, is_vegetarian: false },
    { name: 'Pancake Stack', price: 350, description: 'Buttermilk pancakes with maple syrup and berries', is_signature: false, is_vegetarian: true },
    { name: 'Fresh Fruit Bowl', price: 250, description: 'Seasonal fruits with honey yogurt', is_signature: false, is_vegetarian: true },
  ],
  lunch: [
    { name: 'Koraput Chicken Curry', price: 650, description: 'Slow-cooked with tribal spices', is_signature: true, is_vegetarian: false },
    { name: 'Grilled Fish', price: 750, description: 'Fresh catch with herb butter', is_signature: false, is_vegetarian: false },
    { name: 'Vegetable Biryani', price: 450, description: 'Fragrant rice with garden vegetables', is_signature: false, is_vegetarian: true },
    { name: 'Caesar Salad', price: 350, description: 'Crisp romaine with classic dressing', is_signature: false, is_vegetarian: true },
  ],
  dinner: [
    { name: 'Lamb Rogan Josh', price: 850, description: 'Kashmiri-style braised lamb', is_signature: true, is_vegetarian: false },
    { name: 'Grilled Prawns', price: 950, description: 'Jumbo prawns with garlic butter', is_signature: false, is_vegetarian: false },
    { name: 'Paneer Tikka Masala', price: 550, description: 'Cottage cheese in rich tomato gravy', is_signature: false, is_vegetarian: true },
    { name: 'Pasta Primavera', price: 500, description: 'Fresh vegetables in white wine sauce', is_signature: false, is_vegetarian: true },
  ],
  local: [
    { name: 'Santula', price: 300, description: 'Mixed vegetable stew - tribal specialty', is_signature: true, is_vegetarian: true },
    { name: 'Mudhi Ghanta', price: 350, description: 'Puffed rice preparation with vegetables', is_signature: false, is_vegetarian: true },
    { name: 'Chhena Poda', price: 200, description: 'Caramelized cottage cheese dessert', is_signature: false, is_vegetarian: true },
    { name: 'Macha Jhola', price: 550, description: 'Traditional fish curry with local herbs', is_signature: false, is_vegetarian: false },
  ],
};

const fallbackChefs = [
  {
    name: 'Chef Rajesh Mohanty',
    role: 'Executive Chef',
    image: 'https://static.prod-images.emergentagent.com/jobs/cb75a59d-f3d6-4479-b2b1-b017348b35ba/images/2c474172b8fc5deb98457b8c36b6e73165360aedad26c147b888ab5ab69e5960.png',
    specialty: 'Odia Cuisine',
  },
  {
    name: 'Chef Maria Santos',
    role: 'Pastry Chef',
    image: 'https://static.prod-images.emergentagent.com/jobs/cb75a59d-f3d6-4479-b2b1-b017348b35ba/images/7ea88476001a4bdca53fb6fe01db82a22948603d247f8be779dcc9b170773c48.png',
    specialty: 'French Pastry',
  },
];

const menuCategoryMeta = [
  { id: 'breakfast', name: 'Breakfast', icon: Coffee },
  { id: 'lunch', name: 'Lunch', icon: UtensilsCrossed },
  { id: 'dinner', name: 'Dinner', icon: Wine },
  { id: 'local', name: 'Local Flavours', icon: Leaf },
  { id: 'drinks', name: 'Drinks', icon: Wine },
  { id: 'desserts', name: 'Desserts', icon: Coffee },
];

export default function DiningPage() {
  const [activeMenu, setActiveMenu] = useState('breakfast');
  const [restaurants, setRestaurants] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [chefs, setChefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableCategories, setAvailableCategories] = useState(['breakfast', 'lunch', 'dinner', 'local']);

  useEffect(() => {
    const fetchDiningData = async () => {
      try {
        // Fetch restaurants, menu, and chefs in parallel
        const [restaurantsRes, menuRes, chefsRes] = await Promise.all([
          axios.get(`${API_URL}/api/dining/restaurants`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/dining/menu`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/dining/chefs`).catch(() => ({ data: [] }))
        ]);

        // Set restaurants
        if (restaurantsRes.data && restaurantsRes.data.length > 0) {
          setRestaurants(restaurantsRes.data);
        } else {
          setRestaurants(fallbackRestaurants);
        }

        // Group menu items by category
        if (menuRes.data && menuRes.data.length > 0) {
          const grouped = menuRes.data.reduce((acc, item) => {
            if (!acc[item.category]) acc[item.category] = [];
            acc[item.category].push(item);
            return acc;
          }, {});
          setMenuItems(grouped);
          
          // Set available categories based on actual data
          const cats = Object.keys(grouped);
          if (cats.length > 0) {
            setAvailableCategories(cats);
            setActiveMenu(cats[0]);
          }
        } else {
          setMenuItems(fallbackMenu);
        }

        // Set chefs
        if (chefsRes.data && chefsRes.data.length > 0) {
          setChefs(chefsRes.data);
        } else {
          setChefs(fallbackChefs);
        }
      } catch (error) {
        console.error('Failed to fetch dining data:', error);
        setRestaurants(fallbackRestaurants);
        setMenuItems(fallbackMenu);
        setChefs(fallbackChefs);
      } finally {
        setLoading(false);
      }
    };

    fetchDiningData();
  }, []);

  // Get category metadata
  const getCategoryMeta = (categoryId) => {
    return menuCategoryMeta.find(c => c.id === categoryId) || { 
      id: categoryId, 
      name: categoryId.charAt(0).toUpperCase() + categoryId.slice(1), 
      icon: UtensilsCrossed 
    };
  };

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Dining', url: 'https://thehotelemerald.in/dining' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="dining-page">
      {/* SEO Components */}
      <SEOHead 
        title="Dining & Restaurants | The Hotel Emerald"
        description="Experience culinary excellence at The Hotel Emerald. Multi-cuisine restaurant, authentic Odia flavors, and rooftop bar with panoramic mountain views."
        keywords="hotel restaurant semiliguda, dining koraput, odia food, eastern ghats restaurant"
        url="https://thehotelemerald.in/dining"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <SpeakableSchema 
        cssSelector={[".speakable-content", "h1", "h2", ".restaurant-name", ".menu-item-name"]}
        url="https://thehotelemerald.in/dining"
      />
      {restaurants.map(restaurant => (
        <RestaurantSchema key={restaurant.id} restaurant={restaurant} />
      ))}
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/29685045/pexels-photo-29685045.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Mutton Handi - Traditional Indian Cuisine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/60" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Culinary Excellence
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4"
          >
            Dining & Experiences
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-emerald-100 max-w-xl mx-auto"
          >
            A culinary journey through the flavors of Odisha and beyond
          </motion.p>
        </div>
      </section>

      {/* Restaurants Section */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Our Venues</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              Restaurants & Bars
            </h2>
          </motion.div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading...</div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {restaurants.map((restaurant, index) => (
                <motion.div
                  key={restaurant.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                  data-testid={`restaurant-card-${restaurant.id}`}
                >
                  <div className="relative overflow-hidden rounded-sm aspect-[4/3] mb-4">
                    <img
                      src={restaurant.image}
                      alt={restaurant.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <h3 className="font-serif text-xl mb-1">{restaurant.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-emerald-200">
                        <Clock className="w-4 h-4" />
                        {restaurant.hours}
                      </div>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mb-3">{restaurant.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {(restaurant.features || []).map((feature) => (
                      <span key={feature} className="text-xs bg-muted px-2 py-1 rounded-sm">
                        {feature}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Menu Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Taste</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
              Our Menu
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Explore our curated selection of dishes crafted with the finest ingredients
            </p>
          </motion.div>

          {!loading && Object.keys(menuItems).length > 0 && (
            <Tabs value={activeMenu} onValueChange={setActiveMenu} className="w-full">
              <TabsList className="w-full justify-center mb-12 bg-transparent gap-4 flex-wrap h-auto">
                {availableCategories.map((catId) => {
                  const cat = getCategoryMeta(catId);
                  const IconComponent = cat.icon;
                  return (
                    <TabsTrigger
                      key={cat.id}
                      value={cat.id}
                      className="data-[state=active]:bg-emerald-900 data-[state=active]:text-white px-6 py-3"
                      data-testid={`menu-tab-${cat.id}`}
                    >
                      <IconComponent className="w-4 h-4 mr-2" />
                      {cat.name}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {availableCategories.map((catId) => (
                <TabsContent key={catId} value={catId}>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto"
                  >
                    {(menuItems[catId] || []).map((item, index) => (
                      <motion.div
                        key={item.id || item.name}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex justify-between items-start p-4 bg-card rounded-sm border border-border"
                        data-testid={`menu-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-serif text-foreground">{item.name}</h4>
                            {item.is_signature && (
                              <Star className="w-4 h-4 text-gold fill-gold" />
                            )}
                            {item.is_vegetarian && (
                              <Leaf className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                        </div>
                        <span className="text-emerald-700 dark:text-emerald-400 font-medium ml-4">
                          ₹{item.price}
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>
      </section>

      {/* Chef Section */}
      {chefs.length > 0 && (
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">The Team</p>
              <h2 className="font-serif text-3xl md:text-4xl text-foreground">
                Meet Our Chefs
              </h2>
            </motion.div>

            <div className="flex justify-center gap-12 flex-wrap">
              {chefs.map((chef, index) => (
                <motion.div
                  key={chef.id || chef.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                  data-testid={`chef-${chef.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="relative mb-4">
                    <img
                      src={chef.image}
                      alt={chef.name}
                      className="w-40 h-40 rounded-full object-cover border-4 border-gold"
                    />
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-emerald-900 text-white px-3 py-1 rounded-sm">
                      <ChefHat className="w-4 h-4 inline mr-1" />
                      <span className="text-xs">{chef.specialty}</span>
                    </div>
                  </div>
                  <h3 className="font-serif text-lg text-foreground">{chef.name}</h3>
                  <p className="text-sm text-muted-foreground">{chef.role}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
